/**
 * The browser's back button steps back through the game's screens instead of leaving the page.
 * While there is a way back from where the player is, an entry of the game's own lies on the
 * page's in the history, and going back from it to the page's takes that way back (going forward
 * onto the entry again if there is still one). With none, at the title screen, the entry is
 * lifted off, so that back leaves the page as usual.
 */

/** Marks the game's own entry in the history. */
const MARK = "kneading-needed.back";

/**
 * Follows the back button with `wayBack` (null with nowhere to go back to): the function
 * returned lays or lifts the game's entry as ways back come and go, and is called each frame.
 */
export function followBack(wayBack: () => (() => void) | null): () => void {
  // Reloaded on the game's own entry: it stands for the page's from now on.
  if (history.state?.[MARK]) history.replaceState(null, "");
  /**
   * The game's entry lies on the page's; the game is moving between the two (lifting its entry
   * off, or going forward onto it again).
   */
  let laid = false, moving = false;
  const follow = () => {
    if (moving) return;
    const way = wayBack() !== null;
    if (way && !laid) {
      history.pushState({ [MARK]: true }, "");
      laid = true;
    } else if (!way && laid) {
      moving = true;
      history.back();
    }
  };
  addEventListener("popstate", (e) => {
    laid = e.state?.[MARK] === true;
    if (moving) moving = false;
    else if (!laid) {
      // Gone back by the player: the way back taken, and with one still left, forward onto the
      // game's entry again. Laid anew, with no click on the page since, it would have Chrome
      // skip the page's entry under it, and the next back leave the page.
      wayBack()?.();
      if (wayBack()) {
        moving = true;
        history.forward();
        return;
      }
    }
    follow();
  });
  return follow;
}
